import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Inbox, Mail, ShieldAlert, ArrowRight } from "lucide-react";

interface Stats {
  newContacts: number;
  contacts7d: number;
  emails7d: number;
  failed7d: number;
  suppressed: number;
}

const AdminDashboard = () => {
  const { user, role } = useAuth();
  const isAdmin = role === "admin";
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const since = new Date();
      since.setDate(since.getDate() - 7);
      const sinceIso = since.toISOString();

      const contactPromises = [
        supabase
          .from("contact_submissions")
          .select("id", { count: "exact", head: true })
          .eq("status", "new"),
        supabase
          .from("contact_submissions")
          .select("id", { count: "exact", head: true })
          .gte("created_at", sinceIso),
      ] as const;

      if (!isAdmin) {
        const [newContacts, contacts7d] = await Promise.all(contactPromises);
        if (!active) return;
        setStats({
          newContacts: newContacts.count ?? 0,
          contacts7d: contacts7d.count ?? 0,
          emails7d: 0,
          failed7d: 0,
          suppressed: 0,
        });
        return;
      }

      const [newContacts, contacts7d, emails, suppressed] = await Promise.all([
        ...contactPromises,
        supabase
          .from("email_send_log")
          .select("message_id,status,created_at")
          .gte("created_at", sinceIso)
          .order("created_at", { ascending: false })
          .limit(5000),
        supabase
          .from("suppressed_emails")
          .select("id", { count: "exact", head: true }),
      ]);

      // Dedupe emails by message_id (latest status per email)
      const seen = new Set<string>();
      let total = 0;
      let failed = 0;
      for (const row of emails.data ?? []) {
        const key = row.message_id ?? Math.random().toString();
        if (seen.has(key)) continue;
        seen.add(key);
        total++;
        if (["dlq", "failed", "bounced", "complained"].includes(row.status))
          failed++;
      }

      if (!active) return;
      setStats({
        newContacts: newContacts.count ?? 0,
        contacts7d: contacts7d.count ?? 0,
        emails7d: total,
        failed7d: failed,
        suppressed: suppressed.count ?? 0,
      });
    })();
    return () => {
      active = false;
    };
  }, [isAdmin]);

  const cards = [
    { label: "New contacts", value: stats?.newContacts, sub: "awaiting reply", to: "/admin/contacts", alert: false },
    { label: "Contacts (7d)", value: stats?.contacts7d, sub: "last 7 days", to: "/admin/contacts", alert: false },
    ...(isAdmin
      ? [
          { label: "Emails sent (7d)", value: stats?.emails7d, sub: "last 7 days", to: "/admin/emails", alert: false },
          { label: "Email failures (7d)", value: stats?.failed7d, sub: "needs attention", to: "/admin/emails", alert: (stats?.failed7d ?? 0) > 0 },
          { label: "Suppressed addresses", value: stats?.suppressed, sub: "blocked", to: "/admin/emails/suppressions", alert: false },
        ]
      : []),
  ];

  const firstName = (() => {
    const local = user?.email?.split("@")[0] ?? "";
    const first = local.split(/[._-]/)[0];
    if (!first) return null;
    return first.charAt(0).toUpperCase() + first.slice(1);
  })();

  return (
    <>
      <Helmet>
        <title>Admin dashboard · AI-labbet</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <header className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight">
          Welcome back{firstName ? `, ${firstName}` : ""}
        </h1>
        <p className="text-muted-foreground mt-1">{user?.email}</p>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {cards.map((c) => (
          <Link
            key={c.label}
            to={c.to}
            className={`group rounded-lg border bg-card p-5 hover:bg-card/60 transition-colors ${
              c.alert ? "border-destructive/50 hover:border-destructive" : "border-border hover:border-primary/40"
            }`}
          >
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</div>
            <div className={`text-3xl font-semibold mt-2 ${c.alert ? "text-destructive" : ""}`}>
              {c.value === undefined ? "…" : c.value}
            </div>
            <div className="text-xs text-muted-foreground mt-1 flex items-center justify-between">
              <span>{c.sub}</span>
              <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </Link>
        ))}
      </section>


      <section className={`grid grid-cols-1 ${isAdmin ? "sm:grid-cols-3" : "sm:grid-cols-1"} gap-3`}>
        <Button asChild variant="outline">
          <Link to="/admin/contacts">
            <Inbox className="w-4 h-4 mr-2" /> View contacts
          </Link>
        </Button>
        {isAdmin && (
          <>
            <Button asChild variant="outline">
              <Link to="/admin/emails">
                <Mail className="w-4 h-4 mr-2" /> Email
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/emails/suppressions">
                <ShieldAlert className="w-4 h-4 mr-2" /> Suppressions
              </Link>
            </Button>
          </>
        )}
      </section>
    </>
  );
};

export default AdminDashboard;
